// Pure helpers for the Ask Masira microphone. No imports so they run under `deno test`.

export const MAX_RECORDING_MS = 120_000;

export type VoiceState = 'idle' | 'recording' | 'transcribing';
export type VoiceEvent = 'start' | 'stop' | 'done' | 'fail';

export function voiceReducer(state: VoiceState, event: VoiceEvent): VoiceState {
  switch (state) {
    case 'idle': return event === 'start' ? 'recording' : 'idle';
    case 'recording': return event === 'stop' ? 'transcribing' : event === 'fail' ? 'idle' : 'recording';
    case 'transcribing': return event === 'done' || event === 'fail' ? 'idle' : 'transcribing';
  }
}

const MIME_PREFERENCE = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

/** First MediaRecorder container the browser supports, or null (then the mic is hidden). */
export function pickMimeType(isSupported: (type: string) => boolean): string | null {
  return MIME_PREFERENCE.find((t) => isSupported(t)) ?? null;
}

export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** Append dictated text to whatever is already in the chat box. */
export function appendTranscript(current: string, text: string): string {
  const add = text.trim();
  if (!add) return current;
  const base = current.trimEnd();
  return base ? `${base} ${add}` : add;
}

export function describeVoiceError(status: number | undefined, message: string | undefined): string {
  switch (status) {
    case 503: return "Voice input isn't set up. Ask your administrator.";
    case 413: return 'That recording is too long. Keep it under about 2 minutes.';
    case 422: return 'No speech detected. Try again.';
    case 403: return "Voice input isn't enabled for your organization. Ask your administrator.";
    case 429: return 'Too many voice requests. Try again shortly.';
    default: return message && message.length > 0 ? message : 'Voice input failed. Please try again.';
  }
}

/** True only if at least one recorded chunk contains data. */
export function hasRecordedAudio(sizes: number[]): boolean {
  return sizes.some((s) => s > 0);
}
